import { useCallback, useState } from "react";
import { useStore } from "react-redux";

import { AsyncStatus, PromiseStatus } from "constants/asyncStatus.constants";
import { EvaSource } from "constants/charts.enums";
import { ProductNames } from "constants/forecasts.constants";
import { RootState } from "store/rootReducer";

import { calculateAveragePeakRate, fetchWellPeakRates } from "api/smartQi";

import { PdenSourceEnum } from "models/pdenDataSourceSetting";

export interface SmartQiValues {
  [ProductNames.Oil]?: number;
  [ProductNames.Gas]?: number;
  [ProductNames.Water]?: number;
  [ProductNames.Condensate]?: number;
  [ProductNames.SalesGas]?: number;
  [ProductNames.Steam]?: number;
}

type UseSmartQiReturn =
  | {
      smartQiValues: SmartQiValues;
      status: AsyncStatus.Idle | AsyncStatus.Loading;
      error: null;
      fetchSmartQi: (product?: ProductNames) => Promise<number | null>;
    }
  | {
      smartQiValues: SmartQiValues;
      status: AsyncStatus.Success;
      error: null;
      fetchSmartQi: (product?: ProductNames) => Promise<number | null>;
    }
  | {
      smartQiValues: SmartQiValues;
      status: AsyncStatus.Error;
      error: string;
      fetchSmartQi: (product?: ProductNames) => Promise<number | null>;
    };

/**
 * Custom hook to fetch and manage smart Qi values based on filtered wells
 * Smart Qi values are local to the hook instance (ephemeral — computed within the SmartQi flow)
 *
 * @returns {UseSmartQiReturn} Object containing:
 *   - smartQiValues: Calculated Qi values by product type (local state)
 *   - status: Current status of the fetch operation
 *   - error: Error message if fetch failed, null otherwise
 *   - fetchSmartQi: Function to manually trigger fetch
 */
export function useSmartQi(): UseSmartQiReturn {
  // smartQiValues is ephemeral — local computation within the SmartQi flow
  const [smartQiValues, setSmartQiValues] = useState<SmartQiValues>({});
  const [status, setStatus] = useState<AsyncStatus>(AsyncStatus.Idle);
  const [error, setError] = useState<string | null>(null);

  // Get store instance to read latest state inside callbacks
  const store = useStore<RootState>();

  const SMART_QI_PAGE_INDEX = 0;
  const SMART_QI_PAGE_SIZE = 26190;

  const getPdenSource = (source: EvaSource): PdenSourceEnum => {
    switch (source) {
      case EvaSource.Private:
        return PdenSourceEnum.Private;
      case EvaSource.Hybrid:
        return PdenSourceEnum.Hybrid;
      case EvaSource.Public:
      default:
        return PdenSourceEnum.Public;
    }
  };

  const getProductField = (product: ProductNames): string => {
    const fieldMap = {
      [ProductNames.Oil]: "Performance.Oil.PeakCalRate",
      [ProductNames.Gas]: "Performance.Gas.PeakCalRate",
      [ProductNames.Water]: "Performance.Water.PeakCalRate",
      [ProductNames.Condensate]: "Performance.Oil.PeakCalRate", // Condensate uses Oil peak rate
      [ProductNames.SalesGas]: "Performance.SalesGas.PeakCalRate",
      [ProductNames.Steam]: "Performance.SteamInj.PeakCalRate"
    };
    return fieldMap[product];
  };

  /**
   * Fetches smart Qi values for a specific product or all products
   * Reads the current filter ID from Redux at call time to ensure it uses the latest filter
   * @param product - Optional specific product to fetch. If not provided, fetches all products
   * @returns The fetched Smart Qi value for the specified product, or null if multiple products or error
   */
  const fetchSmartQi = useCallback(
    async (product?: ProductNames): Promise<number | null> => {
      // Read the current filter ID directly from Redux store at call time
      // This ensures we always use the latest filter ID, even if the map/filter changed
      const state = store.getState();
      const currentFilterId = state.filter.filterId?.id;
      const currentPdenDataSource = state.app.pdenDataSourceSetting;

      if (!currentFilterId) {
        setStatus(AsyncStatus.Error);
        setError("No filter available");
        return null;
      }

      setStatus(AsyncStatus.Loading);
      setError(null);

      try {
        // If a specific product is provided, only fetch that one; otherwise fetch all
        const products = product
          ? [product]
          : [
              ProductNames.Oil,
              ProductNames.Gas,
              ProductNames.Water,
              ProductNames.Condensate,
              ProductNames.SalesGas,
              ProductNames.Steam
            ];
        const pdenSource = getPdenSource(currentPdenDataSource.source);
        // The filter's own count: the legend sum leaves out wells Hide Others hides, and
        // a short page would silently drop them from the average.
        const filterCounts = state.filter.filterCounts;
        const pageSize =
          (filterCounts?.Active ?? filterCounts?.UniqueId) || SMART_QI_PAGE_SIZE; // Fallback to default if no wells

        // Fetch peak rates for specified product(s) in parallel
        const results = await Promise.allSettled(
          products.map(async (prod) => {
            const productField = getProductField(prod);
            const responseData = await fetchWellPeakRates(
              currentFilterId,
              productField,
              pageSize,
              SMART_QI_PAGE_INDEX,
              pdenSource
            );

            const avgRate = calculateAveragePeakRate(responseData);

            return { product: prod, avgRate };
          })
        );

        // Process results - merge with existing local values instead of replacing
        let newSmartQiValues: SmartQiValues;
        let fetchedValue: number | null = null;

        setSmartQiValues((prev) => {
          newSmartQiValues = { ...prev };
          results.forEach((result) => {
            if (
              result.status === PromiseStatus.Fulfilled &&
              result.value.avgRate !== null
            ) {
              newSmartQiValues[result.value.product] = result.value.avgRate;
              // If fetching a specific product, store its value to return
              if (product && result.value.product === product) {
                fetchedValue = result.value.avgRate;
              }
            }
          });
          return newSmartQiValues;
        });

        setStatus(AsyncStatus.Success);

        // Return the fetched value for single product fetch
        return fetchedValue;
      } catch (err) {
        setStatus(AsyncStatus.Error);
        setError(err instanceof Error ? err.message : "Failed to fetch smart Qi values");
        return null;
      }
    },
    [store]
  );

  if (status === AsyncStatus.Error) {
    return {
      smartQiValues,
      status: AsyncStatus.Error,
      error: error || "Unknown error",
      fetchSmartQi
    };
  }

  return {
    smartQiValues,
    status,
    error: null,
    fetchSmartQi
  };
}
